import React from 'react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { getFileIcon, formatFileSize, formatDate, isPreviewable } from '../../lib/libraryUtils';

/**
 * Document table component
 */
const DocumentTable = ({
  documents,
  loading,
  emptyMessage = 'Geen documenten gevonden',
  onDocumentClick,
  onPreview,
  onDownload,
  onRename,
  onMove,
  onDelete,
}) => {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <span className="text-4xl">📄</span>
        <p className="mt-2">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Document
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
              Type
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
              Grootte
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
              Gewijzigd
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Acties
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {documents.map(doc => (
            <DocumentRow
              key={doc.id}
              document={doc}
              onClick={() => onDocumentClick?.(doc)}
              onPreview={() => onPreview?.(doc)}
              onDownload={() => onDownload?.(doc)}
              onRename={() => onRename?.(doc)}
              onMove={() => onMove?.(doc)}
              onDelete={() => onDelete?.(doc)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DocumentRow = ({
  document,
  onClick,
  onPreview,
  onDownload,
  onRename,
  onMove,
  onDelete,
}) => {
  const canPreview = isPreviewable(document.extension);

  return (
    <tr 
      className="hover:bg-gray-50 cursor-pointer"
      onClick={onClick}
      data-testid={`document-${document.id}`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{getFileIcon(document.extension)}</span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {document.title || document.original_name}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {document.original_name}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 hidden sm:table-cell">
        <span className="text-xs uppercase text-gray-500 bg-gray-100 px-2 py-1 rounded">
          {document.extension}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell">
        {formatFileSize(document.cloudinary_bytes || document.size)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell">
        {formatDate(document.updated_at)}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {canPreview && (
            <Button variant="ghost" size="sm" onClick={onPreview} title="Preview">
              👁️
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onDownload} title="Download">
            ⬇️
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">⋮</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onRename}>
                ✏️ Hernoemen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onMove}>
                📦 Verplaatsen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDelete} className="text-red-600">
                🗑️ Verwijderen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
};

export default DocumentTable;
